/**
 * Moves an element to a new position with a specified speed and returns a Promise that resolves when the animation is complete.
 * @param {HTMLElement} element - The element to move.
 * @param {number} newX - The new X-coordinate of the element.
 * @param {number} newY - The new Y-coordinate of the element.
 * @param {number} speed - The speed in pixels per second.
 * @returns {Promise} A Promise that resolves when the animation is complete.
 */
import {distanceToInches, parseImperialFractionToDecimal} from "./sqftHelper";
import EventManager from "../components/canvas/store/sqftEventManager";
import sqftConfig from "../components/canvas/store/sqftConfig";
import {MEASURE_UNITS} from "../constants";

export const moveElement = (element, newX, newY, speed) => {
  return new Promise(resolve => {
    // Get the current coordinates of the element
    const oldX = element.offsetLeft;
    const oldY = element.offsetTop;

    // Calculate the distance between the old and new coordinates
    const distanceX = newX - oldX;
    const distanceY = newY - oldY;

    // Calculate the duration of the animation based on the speed
    const duration = Math.sqrt(distanceX ** 2 + distanceY ** 2) / speed;

    // Set the element to the initial position
    element.style.position = "absolute";
    element.style.left = oldX + "px";
    element.style.top = oldY + "px";

    // Animate the element to the new position
    const startTime = performance.now();
    function animate(currentTime) {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const x = oldX + distanceX * progress;
      const y = oldY + distanceY * progress;
      element.style.left = x + "px";
      element.style.top = y + "px";
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve(); // Animation complete, resolve the Promise
      }
    }
    requestAnimationFrame(animate);
  });
}

export const delay = (time) => {
  return new Promise(resolve => setTimeout(resolve, time));
}

export function createAdjustableInput({
                                        position = { x: 0, y: 0 },
                                        initialValue = "",
                                        min = 1,
                                        max = 1000,
                                        orientation = "horizontal",
                                        onChange = () => {},
                                        onClose = () => {},
                                        step = 1,
                                        longPressInterval = 50,
                                        longPressDelay = 200,
                                      }) {
  const objectName = { name: 'lineInput' }; // Unique identifier for EventManager
  let isClosed = false; // Flag to prevent duplicate close calls

  const inputWrapper = document.createElement("div"); // Wrapper for input and buttons
  inputWrapper.className = "line-input";
  inputWrapper.style.position = "absolute";
  inputWrapper.style.left = `${position.x}px`;
  inputWrapper.style.top = `${position.y}px`;

  // Set orientation class
  if (orientation === "horizontal") {
    inputWrapper.classList.add("line-input-horizontal");
  } else if (orientation === "vertical") {
    inputWrapper.classList.add("line-input-vertical");
  }

  // Create input field
  const input = document.createElement("input");
  input.type = "text";
  input.className = "line-input-field";
  input.setAttribute("min", min.toString());
  input.setAttribute("max", max.toString());
  input.value = initialValue;

  // Adjust input value within [min, max] bounds and trigger onChange
  const adjustInputValue = (step) => {
    let currentValue = parseImperialFractionToDecimal(input.value);
    currentValue += step;
    currentValue = Math.max(min, Math.min(currentValue, max)); // Ensure within bounds
    input.value = currentValue;
    onChange(currentValue);
    input.dispatchEvent(new Event("change")); // Trigger change event for UI updates

    if (sqftConfig.measureUnit === MEASURE_UNITS.Imperial) {
      input.value = distanceToInches(currentValue).replace("\"", "");
    } else {
      input.value = currentValue;
    }
  };

  let longPressTimeout;
  let delayTimeout;
  const startLongPress = (step) => {
    adjustInputValue(step);
    delayTimeout = setTimeout(() => {
      longPressTimeout = setInterval(() => adjustInputValue(step), longPressInterval);
    }, longPressDelay);
  };
  const stopLongPress = () => {
    clearInterval(longPressTimeout);
    clearTimeout(delayTimeout);
    input.focus();
  };

  // Create increment button with long press functionality
  const incrementButton = document.createElement("button");
  incrementButton.className = "increment-button";
  incrementButton.textContent = "▲";
  incrementButton.onmousedown = () => startLongPress(step);
  incrementButton.onmouseup = stopLongPress;
  incrementButton.onmouseleave = stopLongPress;

  // Create decrement button with long press functionality
  const decrementButton = document.createElement("button");
  decrementButton.className = "decrement-button";
  decrementButton.textContent = "▼";
  decrementButton.onmousedown = () => startLongPress(-step);
  decrementButton.onmouseup = stopLongPress;
  decrementButton.onmouseleave = stopLongPress;

  // Append elements to wrapper
  inputWrapper.appendChild(input);
  inputWrapper.appendChild(incrementButton);
  inputWrapper.appendChild(decrementButton);

  // Use onchange instead of input for final validation after user finishes typing
  input.onchange = (event) => {
    let value = parseImperialFractionToDecimal(event.target.value);
    value = Math.max(min, Math.min(value, max)); // Ensure within bounds
    input.value = value;
    onChange(value);
  };

  // Close input when pressing Enter or if blurred outside of increment/decrement buttons
  const closeInput = () => {
    if (isClosed) return; // Prevent duplicate calls
    isClosed = true;

    onClose();
    inputWrapper.remove();
    EventManager.removeEvent(objectName);
    document.removeEventListener("mousedown", handleOutsideClick);
  };

  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      input.blur(); // Explicitly trigger blur to ensure onchange fires
      closeInput();
    }
  });

  const handleOutsideClick = (event) => {
    if (!inputWrapper.contains(event.target)) {
      closeInput();
    }
  };

  // Add event to close input on MOUSE_DOWN outside, unless the input is focused
  EventManager.addEvent(
    EventManager.events.MOUSE_DOWN,
    closeInput,
    objectName,
    EventManager.priorities.OVERALL
  );
  document.addEventListener("mousedown", handleOutsideClick);

  return inputWrapper;
}
